import { useState } from 'react';
import { Check, Copy, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';

// Stage 7A D3 TASK 4 — publish success. Shows the Meta ad id (with copy), a deep
// link into Meta Ads Manager, and "Back to drafts". Closing (X/Escape) leaves the
// AM on the now read-only published compose page.

type Props = {
  open: boolean;
  publishedMetaAdId: string | null;
  adsManagerUrl: string | null;
  onBackToDrafts: () => void;
  onClose: () => void;
};

export function PublishSuccessModal({
  open,
  publishedMetaAdId,
  adsManagerUrl,
  onBackToDrafts,
  onClose,
}: Props) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!publishedMetaAdId) return;
    try {
      await navigator.clipboard.writeText(publishedMetaAdId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — id is still visible to copy manually */
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ad published to Meta</DialogTitle>
          <DialogDescription>Your ad is now live in Meta Ads Manager.</DialogDescription>
        </DialogHeader>

        {publishedMetaAdId && (
          <div>
            <p className="mb-1 text-xs font-medium text-slate-500">Meta ad ID</p>
            <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <code className="flex-1 truncate text-sm text-slate-800">{publishedMetaAdId}</code>
              <button
                type="button"
                onClick={copy}
                aria-label="Copy Meta ad ID"
                className="rounded p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-700"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onBackToDrafts}>
            Back to drafts
          </Button>
          {adsManagerUrl && (
            <Button asChild>
              <a href={adsManagerUrl} target="_blank" rel="noopener noreferrer">
                View in Meta Ads Manager
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
